<template>
  <div class="circular-business-models-page">
    <WeCircularNavbar />

    <section class="page-section hero-section">
      <div class="page-container">
        <h1 class="page-title">Circular Business Models</h1>

        <div class="page-intro">
          <p>
            Through the active participation of female start-ups and stakeholders in the 12 WE.Circular Labs, six
            business models were developed within the WE.Circular project to accelerate the transition to a circular
            economy among women entrepreneurs. The validation process in the Labs has ensured that each model
            effectively responds to real business needs and supports the transition toward circularity.
          </p>

          <p>
            The 6 circular business models represent different approaches and solutions that contribute to
            transformation in the project’s three main thematic areas:
          </p>
        </div>

        <div class="thematic-list">
          <div class="thematic-item">
            <span class="thematic-item__bullet"></span>
            <div class="thematic-item__content">
              <strong>Raw materials</strong>
              <span>
                evaluating how the models encourage responsible sourcing, reuse of materials, and reduction of virgin
                resource consumption;
              </span>
            </div>
          </div>

          <div class="thematic-item">
            <span class="thematic-item__bullet"></span>
            <div class="thematic-item__content">
              <strong>Production processes</strong>
              <span>
                assessing the extent to which the models improve production efficiency, integrate lifecycle thinking,
                and minimize resource losses;
              </span>
            </div>
          </div>

          <div class="thematic-item">
            <span class="thematic-item__bullet"></span>
            <div class="thematic-item__content">
              <strong>Waste management</strong>
              <span>
                analysing how the models enable reuse, repair, and valorisation of waste and by-products, and promote
                systems for recovery and circular loops.
              </span>
            </div>
          </div>
        </div>

        <p class="section-label">These are the following:</p>

        <div class="models-grid">
          <article v-for="(model, index) in models" :key="'model-' + index" class="model-card">
            <div class="model-card__header">
              <h2 class="model-card__header-text">{{ model.label }}</h2>
            </div>

            <div class="model-card__body">
              <p class="model-card__title">{{ model.title }}</p>

              <a v-elseó :href="model.route" class="model-card__button" target="_blank" rel="noopener noreferrer">
                Learn More
                <span aria-hidden="true">→</span>
              </a>
            </div>
          </article>
        </div>
      </div>
    </section>

    <WeCircularFooter />
  </div>
</template>

<script>
import WeCircularNavbar from './WeCircularNavbar.vue'
import WeCircularFooter from './WeCircularFooter.vue'

export default {
  name: 'CircularBusinessModelsPage',
  components: {
    WeCircularNavbar,
    WeCircularFooter
  },
  data() {
    return {
      models: [
        {
          label: 'Business Model I.',
          title: 'Circular Design Accelerators',
          route: 'https://vali.hu/static/1_BM_O.2.1_Innovative_Business_Models_for_circular_transition_of_WE.pdf'
        },
        {
          label: 'Business Model II.',
          title: 'Resource Champions',
          route: 'https://vali.hu/static/2_BM_PDFsam_O.2.1_Innovative_Business_Models_for_circular_transition_of_WE.pdf'
        },
        {
          label: 'Business Model III.',
          title: 'Production Optimizers',
          route: 'https://vali.hu/static/3_BM_PDFsam_O.2.1_Innovative_Business_Models_for_circular_transition_of_WE.pdf'
        },
        {
          label: 'Business Model IV.',
          title: 'Smart Digital Enablers',
          route: 'https://vali.hu/static/4_BM_PDFsam_O.2.1_Innovative_Business_Models_for_circular_transition_of_WE.pdf'
        },
        {
          label: 'Business Model V.',
          title: 'Market Educators',
          route: 'https://vali.hu/static/5_BM_PDFsam_O.2.1_Innovative_Business_Models_for_circular_transition_of_WE.pdf'
        },
        {
          label: 'Business Model VI.',
          title: 'Collaborative Ecosystems',
          route: 'https://vali.hu/static/6_BM_PDFsam_O.2.1_Innovative_Business_Models_for_circular_transition_of_WE.pdf'
        }
      ]
    }
  }
}
</script>

<style scoped>
.circular-business-models-page {
  min-height: 100vh;
  background: #f4f7f8;
}

.page-section {
  width: 100%;
}

.page-container {
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 40px 32px 64px;
  box-sizing: border-box;
}

.hero-section {
  padding-top: 24px;
}

.back-button {
  align-self: flex-start;
  display: flex;
  align-items: center;
  justify-content: flex-start !important;
  height: 30px;
  min-width: 66px;
  padding: 0 12px;
  margin-bottom: 22px;
  border: 1px solid #003399;
  border-radius: 8px;
  background: transparent;
  color: #003399;
  line-height: 1;
  cursor: pointer;
  margin-left: 0;
}

.page-title {
  margin: 0 0 18px;
  font-size: 42px;
  line-height: 1.12;
  font-weight: 700;
  color: #0f4c97;
  text-align: left;
}

.page-intro {
  max-width: 860px;
  margin-bottom: 26px;
}

.page-intro p {
  margin: 0 0 14px;
  font-size: 18px;
  line-height: 1.75;
  color: #4f6f8f;
  text-align: left;
}

.page-intro p:last-child {
  margin-bottom: 0;
}

.thematic-list {
  max-width: 860px;
  margin-bottom: 32px;
}

.thematic-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.thematic-item + .thematic-item {
  margin-top: 16px;
}

.thematic-item__bullet {
  width: 8px;
  height: 8px;
  margin-top: 9px;
  flex: 0 0 8px;
  border-radius: 50%;
  background: #0f4c97;
}

.thematic-item__content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #4f6f8f;
  font-size: 17px;
  line-height: 1.7;
  text-align: left;
}

.thematic-item__content strong {
  color: #0f4c97;
  font-weight: 700;
}

.section-label {
  margin: 0 0 24px;
  font-size: 18px;
  line-height: 1.5;
  font-weight: 700;
  color: #0f4c97;
  text-align: left;
}

.models-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.model-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 278px;
  border: 1px solid #0d666b;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 6px 18px rgba(17, 76, 111, 0.06);
}

.model-card__header {
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  min-height: 112px;
  padding: 18px 18px 10px;
  overflow: hidden;
  background: url('../../assets/img/we-circular/16-9@4x.png') center 30% / cover no-repeat;
}

.model-card__header::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(13, 92, 95, 0.72);
}

.model-card__header-text {
  position: relative;
  z-index: 1;
  color: #ffffff;
  font-size: 24px;
  line-height: 1.25;
  font-weight: 600;
  text-align: center;
}

.model-card__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 18px 18px 22px;
  text-align: center;
}

.model-card__title {
  margin: 0;
  font-size: 20px;
  line-height: 1.2;
  font-weight: 600;
  color: #0f4c97;
  text-align: center;
}

.model-card__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 98px;
  min-height: 34px;
  padding: 0 14px;
  border-radius: 6px;
  background: #0f4c97;
  color: #ffffff;
  font-size: 14px;
  line-height: 1;
  font-weight: 600;
  text-decoration: none;
}

.model-card__button:hover {
  background: #0c3d79;
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(15, 76, 151, 0.18);
}

@media (max-width: 1100px) {
  .page-title {
    font-size: 36px;
  }

  .models-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .model-card__title {
    font-size: 24px;
  }
}

@media (max-width: 768px) {
  .page-container {
    padding: 30px 20px 48px;
  }

  .page-title {
    font-size: 30px;
  }

  .page-intro p,
  .thematic-item__content,
  .section-label {
    font-size: 16px;
    line-height: 1.7;
  }

  .models-grid {
    grid-template-columns: 1fr;
  }

  .model-card {
    min-height: 248px;
  }

  .model-card__header {
    min-height: 96px;
  }

  .model-card__header-text {
    font-size: 16px;
  }

  .model-card__body {
    padding: 16px 16px 20px;
  }

  .model-card__title {
    font-size: 22px;
  }

  .model-card__button {
    font-size: 13px;
  }
}
</style>
